/**
 * Targeted slot re-render.
 *
 * A cart-quantity change must not redraw the whole card. Three things break if
 * it does, and none of them are theoretical:
 *
 *  1. `image_zoom_hover.js` binds `.card-img-container`, keeps per-container
 *     state in a WeakMap, appends a `.product-zoom-window` to document.body and
 *     watches the img's src. Teardown/rebuild runs through rfhqDom, which is
 *     rAF-deferred — so replacing the card mid-hover leaves the magnifier
 *     pointing at a node that no longer exists.
 *  2. A keyboard user on the stepper's `+` loses focus on every increment.
 *  3. The image re-decodes and flashes on a slow connection.
 *
 * The architectural rule is unchanged: the RENDERER redraws from the model.
 * Nothing external reaches into a card and edits it. This just narrows what
 * gets redrawn from "the card" to "the slot that actually changed".
 */

import { renderLayout } from './layout/index.js';

const SLOT_ATTR = 'data-rfhq-slot';

/** Capture enough about focus to restore it after a subtree swap. */
function captureFocus(container) {
	const active = document.activeElement;
	if (!active || !container.contains(active)) return null;

	// Prefer a stable, meaningful selector over an index: the actions slot can
	// legitimately change shape (button ⇄ stepper), and an index would then
	// restore focus to the wrong control.
	const dir = active.getAttribute && active.getAttribute('data-dir');
	if (dir) return { kind: 'dir', value: dir };
	if (active.classList && active.classList.contains('cart-qty')) {
		return { kind: 'class', value: 'cart-qty', selectionStart: active.selectionStart };
	}
	return null;
}

function restoreFocus(container, captured) {
	if (!captured) return;

	let next = null;
	if (captured.kind === 'dir') {
		next = container.querySelector(`[data-dir="${captured.value}"]`);
	} else if (captured.kind === 'class') {
		next = container.querySelector(`.${captured.value}`);
	}
	if (!next || typeof next.focus !== 'function') return;

	next.focus();
	if (captured.selectionStart != null && next.setSelectionRange) {
		try {
			next.setSelectionRange(captured.selectionStart, captured.selectionStart);
		} catch (err) {
			/* not a text input — nothing to restore */
		}
	}
}

/**
 * Re-render one slot of an already-rendered card from an updated model.
 *
 * @param {HTMLElement} cardEl  the `.item-card` root
 * @param {string} slot         value of the slot's `data-rfhq-slot`
 * @param {object} model        the updated ProductCardModel
 * @param {object} ctx          the same render context the card was built with
 * @returns {boolean}           false when the slot is not present
 */
export function patchSlot(cardEl, slot, model, ctx) {
	if (!cardEl) return false;

	const target = cardEl.querySelector(`[${SLOT_ATTR}="${slot}"]`);
	if (!target) return false;

	// Render a throwaway card from the new model and lift the matching slot out
	// of it. Doing it this way means a slot is only ever produced by its layout
	// — there is no second code path that knows how to build one.
	const fresh = renderLayout(model, ctx);
	const source = fresh.querySelector(`[${SLOT_ATTR}="${slot}"]`);
	if (!source) return false;

	const captured = captureFocus(target);
	target.replaceChildren(...source.childNodes);
	restoreFocus(target, captured);
	return true;
}

/** True when this card exposes the named slot. */
export function hasSlot(cardEl, slot) {
	return Boolean(cardEl && cardEl.querySelector(`[${SLOT_ATTR}="${slot}"]`));
}
